import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { BookOpenText, ClipboardList, FlaskConical, History, LogOut, Menu, MessageSquare, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

export const NAV_ITEMS = [
  { to: "/chat", label: "Chat", icon: MessageSquare },
  { to: "/evaluaties", label: "Evaluaties", icon: FlaskConical },
  { to: "/runs", label: "Run-geschiedenis", icon: History },
  { to: "/vragenlijsten", label: "Vragenlijsten", icon: ClipboardList },
  { to: "/context", label: "Databasecontext", icon: BookOpenText },
] as const;

export function BrandLogo({ className }: { className?: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className={cn("flex items-center gap-3", className)}>
      {!failed ? (
        <img src="/brand/simon-loos-logo.svg" alt="Simon Loos" className="h-7 w-auto" onError={() => setFailed(true)} />
      ) : (
        <span className="font-display text-lg font-bold tracking-tight">Simon Loos</span>
      )}
      <span className="h-6 w-px bg-border" aria-hidden />
      <span className="font-display text-sm font-semibold tracking-tight text-foreground">BI Agents</span>
    </span>
  );
}

function initials(name: string) {
  const parts = name.split(/[@\s.]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function UserMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  if (!user) return null;
  const name = user.displayName || user.email || "Gebruiker";
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-border bg-sl-yellow text-xs font-semibold text-sl-charcoal hover:ring-2 hover:ring-sl-yellow/50"
        title={name}
        aria-label="Gebruikersmenu"
        aria-expanded={open}
      >
        {user.photoURL ? <img src={user.photoURL} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" /> : initials(name)}
      </button>
      {open && (
        <div className="animate-fade-in absolute right-0 top-full z-50 mt-2 w-60 rounded-lg border border-border bg-card py-1 shadow-pop">
          <div className="border-b border-border px-3 py-2">
            <p className="truncate text-sm font-medium">{user.displayName || "Gebruiker"}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          </div>
          <button
            onClick={() => signOut()}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-destructive"
          >
            <LogOut className="h-4 w-4" />
            Uitloggen
          </button>
        </div>
      )}
    </div>
  );
}

export function AppShell({ children, liveRun }: { children: ReactNode; liveRun?: boolean }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMobileOpen(false), [location.pathname]);

  const navLink = (mobile: boolean) =>
    ({ isActive }: { isActive: boolean }) =>
      cn(
        "relative flex items-center gap-2 text-sm font-medium transition-colors",
        mobile ? "rounded-md px-3 py-2.5" : "h-16 px-3",
        isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground",
        mobile && isActive && "bg-accent",
        !mobile && isActive && "after:absolute after:inset-x-3 after:bottom-0 after:h-[3px] after:rounded-t after:bg-sl-yellow",
      );

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      <header className="relative z-40 shrink-0 border-b border-border bg-card">
        <div className="mx-auto flex h-16 max-w-[1600px] items-center gap-6 px-4 sm:px-6">
          <Link to="/" className="shrink-0" aria-label="Startpagina">
            <BrandLogo />
          </Link>
          <nav className="hidden flex-1 items-center lg:flex" aria-label="Hoofdnavigatie">
            {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} className={navLink(false)}>
                <Icon className="h-4 w-4" />
                {label}
                {to === "/runs" && liveRun && <span className="h-2 w-2 rounded-full bg-sl-red" title="Live evaluatie actief" />}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <UserMenu />
            <button
              className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-muted lg:hidden"
              onClick={() => setMobileOpen((o) => !o)}
              aria-label="Menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <div className="h-[3px] bg-linear-to-r from-sl-yellow via-sl-yellow to-sl-red" aria-hidden />
        {mobileOpen && (
          <nav className="border-b border-border bg-card px-4 py-2 lg:hidden" aria-label="Mobiele navigatie">
            {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} className={navLink(true)}>
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>
      <main className="min-h-0 flex-1 overflow-hidden">{children}</main>
    </div>
  );
}
